import { useCallback, useEffect, useMemo, useState } from "react";
import logoUrl from "/logo-clear.png";

type ServiceDefinition = {
  key: string;
  name: string;
  url: string;
};

type ServiceStatus = "unknown" | "ok" | "degraded" | "error";

type ServiceState = {
  status: ServiceStatus;
  message: string;
  checkedAt?: string;
  latencyMs?: number;
  targetUrl?: string;
};

type AggregatedServiceEntry = {
  key?: string;
  name?: string;
  url?: string;
  status?: unknown;
  message?: string;
  latencyMs?: unknown;
  checkedAt?: unknown;
};

type AggregatedServicesPayload = {
  services?: Record<string, AggregatedServiceEntry>;
};

const envValues = import.meta.env as Record<string, string | undefined>;

const detectStagingDeployment = () => {
  const envHint =
    envValues.VITE_DEPLOY_ENVIRONMENT ??
    envValues.VITE_ENVIRONMENT ??
    envValues.VITE_ENV ??
    "";
  const normalizedHint = envHint.toLowerCase();
  if (normalizedHint === "staging") {
    return true;
  }

  const mode = typeof import.meta.env.MODE === "string" ? import.meta.env.MODE.toLowerCase() : "";
  if (mode.includes("staging")) {
    return true;
  }

  if (typeof window !== "undefined") {
    const hostname = window.location.hostname.toLowerCase();
    if (
      hostname.includes(".staging.") ||
      hostname.startsWith("staging.") ||
      hostname.endsWith(".staging") ||
      hostname.endsWith(".staging.brewskiapp.com")
    ) {
      return true;
    }
  }

  return false;
};

const isStagingDeployment = detectStagingDeployment();

const resolveEnv = (
  key: string,
  defaults: { production: string; staging: string }
) => {
  const raw = envValues[key];
  if (typeof raw === "string" && raw.trim().length > 0) {
    return raw.trim();
  }
  return isStagingDeployment ? defaults.staging : defaults.production;
};

const servicesAggregatorUrl = resolveEnv(
  "VITE_HEALTH_SERVICES_URL",
  {
    production: "https://api.brewskiapp.com/public/health/services",
    staging: "https://api.staging.brewskiapp.com/public/health/services"
  }
);

const serviceDefinitions: ServiceDefinition[] = [
  {
    key: "api",
    name: "Public API",
    url: resolveEnv("VITE_API_HEALTH_URL", {
      production: "https://api.brewskiapp.com/health",
      staging: "https://api.staging.brewskiapp.com/health"
    })
  },
  {
    key: "admin",
    name: "Admin UI",
    url: resolveEnv("VITE_ADMIN_HEALTH_URL", {
      production: "https://admin.brewskiapp.com/health.json",
      staging: "https://admin.staging.brewskiapp.com/health.json"
    })
  },
  {
    key: "show",
    name: "Show Site",
    url: resolveEnv("VITE_SHOW_HEALTH_URL", {
      production: "https://show.brewskiapp.com/health.json",
      staging: "https://show.staging.brewskiapp.com/health.json"
    })
  },
  {
    key: "shop",
    name: "Shop Frontend",
    url: resolveEnv("VITE_SHOP_HEALTH_URL", {
      production: "https://indiebrewer.com/health.json",
      staging: "https://staging.indiebrewer.com/health.json"
    })
  }
];

const statusVariant: Record<ServiceStatus, string> = {
  unknown: "secondary",
  ok: "success",
  degraded: "warning",
  error: "danger"
};

const statusLabel: Record<ServiceStatus, string> = {
  unknown: "Checking",
  ok: "Online",
  degraded: "Degraded",
  error: "Offline"
};

const normalizeStatus = (input: unknown): ServiceStatus => {
  if (typeof input === "string") {
    const lowered = input.toLowerCase();
    if (["ok", "online", "pass", "healthy"].includes(lowered)) {
      return "ok";
    }
    if (["warn", "warning", "degraded"].includes(lowered)) {
      return "degraded";
    }
    if (["error", "fail", "offline", "critical"].includes(lowered)) {
      return "error";
    }
  }

  if (input === true) {
    return "ok";
  }

  if (input === false) {
    return "error";
  }

  return "unknown";
};

const parseAggregatedStates = async (): Promise<Record<string, ServiceState> | null> => {
  const trimmedUrl = servicesAggregatorUrl.trim();
  if (!trimmedUrl) {
    return null;
  }

  try {
    const response = await fetch(trimmedUrl, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      headers: {
        Accept: "application/json"
      }
    });

    if (!response.ok) {
      console.warn(
        `[health] Aggregator request failed with status ${response.status}`
      );
      return null;
    }

    const payload = (await response.json().catch(() => null)) as
      | AggregatedServicesPayload
      | null;

    if (!payload || typeof payload !== "object" || !payload.services) {
      return null;
    }

    const states: Record<string, ServiceState> = {};

    for (const [key, entry] of Object.entries(payload.services)) {
      if (!entry || typeof entry !== "object") {
        continue;
      }

      const status = normalizeStatus(entry.status);
      const messageText =
        typeof entry.message === "string" && entry.message.trim().length > 0
          ? entry.message.trim()
          : `Status: ${statusLabel[status]}`;

      const targetUrl =
        typeof entry.url === "string" && entry.url.trim().length > 0
          ? entry.url.trim()
          : undefined;

      states[key] = {
        status,
        message: messageText,
        checkedAt:
          typeof entry.checkedAt === "string" ? entry.checkedAt : undefined,
        latencyMs:
          typeof entry.latencyMs === "number" ? entry.latencyMs : undefined,
        targetUrl
      };
    }

    return states;
  } catch (error) {
    console.warn("[health] Aggregator request error", error);
    return null;
  }
};

const createPendingState = (definition: ServiceDefinition): ServiceState => ({
  status: "unknown",
  message: "Waiting for first check...",
  targetUrl: definition.url
});

const createUnavailableState = (definition: ServiceDefinition): ServiceState => ({
  status: "error",
  message: "Service health unavailable",
  targetUrl: definition.url
});

const App = () => {
  const definitions = useMemo(() => serviceDefinitions, []);
  const [serviceStates, setServiceStates] = useState<Record<string, ServiceState>>(
    () =>
      Object.fromEntries(
        definitions.map((definition) => [definition.key, createPendingState(definition)])
      )
  );
  const [isLoading, setIsLoading] = useState(false);

  const runChecks = useCallback(async () => {
    setIsLoading(true);
    try {
      const aggregatedStates = await parseAggregatedStates();

      if (aggregatedStates && Object.keys(aggregatedStates).length > 0) {
        const combinedStates = definitions.reduce<Record<string, ServiceState>>(
          (accumulator, definition) => {
            const aggregatedState = aggregatedStates[definition.key];
            if (aggregatedState) {
              accumulator[definition.key] = {
                ...aggregatedState,
                targetUrl: aggregatedState.targetUrl ?? definition.url
              };
            } else {
              accumulator[definition.key] = createUnavailableState(definition);
            }
            return accumulator;
          },
          {}
        );

        setServiceStates(combinedStates);
        return;
      }

      const fallbackStates = definitions.reduce<Record<string, ServiceState>>(
        (accumulator, definition) => {
          accumulator[definition.key] = {
            status: "error",
            message: "Aggregator unavailable",
            targetUrl: definition.url
          };
          return accumulator;
        },
        {}
      );

      setServiceStates(fallbackStates);
    } finally {
      setIsLoading(false);
    }
  }, [definitions]);

  useEffect(() => {
    void runChecks();
  }, [runChecks]);

  return (
    <div className="bg-light min-vh-100">
      <header className="bg-white border-bottom shadow-sm">
        <div className="container py-3 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <img
              src={logoUrl}
              alt="Brewski logo"
              className="rounded"
              style={{ height: 40, width: "auto" }}
            />
            <div>
              <p className="h5 mb-0">Brewski Service Health</p>
              <p className="text-muted small mb-0">
                Monitoring API, Admin, Show, and Shop uptime
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void runChecks()}
            disabled={isLoading}
          >
            {isLoading ? "Refreshing..." : "Reload"}
          </button>
        </div>
      </header>
      <main className="container py-4">
        <div className="row g-4">
          {definitions.map((definition) => {
            const state = serviceStates[definition.key];
            const variant = statusVariant[state?.status ?? "unknown"];
            const checkedUrl = state?.targetUrl ?? definition.url;

            return (
              <div className="col-12 col-md-6" key={definition.key}>
                <div className="card shadow-sm h-100">
                  <div className="card-body d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h2 className="h5 mb-0">{definition.name}</h2>
                      <span className={`badge text-bg-${variant}`}>
                        {statusLabel[state?.status ?? "unknown"]}
                      </span>
                    </div>
                    <p className="text-muted small mb-3">{checkedUrl}</p>
                    <p className="mb-3 flex-grow-1">{state?.message}</p>
                    <div className="d-flex justify-content-between align-items-center mt-auto">
                      <span className="text-muted small">
                        {state?.checkedAt
                          ? `Checked ${new Date(state.checkedAt).toLocaleTimeString()}`
                          : "Pending check"}
                      </span>
                      {typeof state?.latencyMs === "number" && (
                        <span className="text-muted small">
                          Latency {`${Math.round(state.latencyMs)} ms`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default App;
